import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import i18n from '../i18n/i18n';
import { useAuth } from '../hooks/useAuth';
import { useReportes, useListaMaquinas } from '../hooks';
import { useEmpresasContext } from '../context/EmpresasContext';
import { useFiltroMaquinasUsuario } from '../hooks/useFiltroMaquinasUsuario';
import { CrudTable } from '../components/admin/crud';
import { Button, Badge } from '../components/ui';
import { ReportesService } from '../services';
import { formatDateSafe, toISODateSafe } from '../utils/dateUtils';
import type { ReporteBackend } from '../services/reportesService';
import type { ReporteFiltros } from '../types';
import ModalConfirmarEliminar from '../components/modales/ModalConfirmarEliminar';
import ModalOpcionesPDF from '../components/modales/ModalOpcionesPDF';
import { useTranslation } from 'react-i18next';

const ReportesListView: React.FC = () => {
  const { usuario, hasPermission } = useAuth();
  useEmpresasContext();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isLoading: isLoadingFiltro } = useFiltroMaquinasUsuario();

  const columns = useMemo(() => [
    {
      key: 'numero',
      title: t('reports.report_number'),
      width: '150px',
      render: (numero: string) => (
        <div className="font-medium text-blue-600">{numero}</div>
      ),
    },
    {
      key: 'fecha',
      title: t('reports.date'),
      width: '120px',
      render: (fecha: string) => formatDateSafe(fecha),
    },
    {
      key: 'maquina_info',
      title: t('reports.machine'),
      width: '180px',
      render: (_: unknown, row: ReporteBackend) => (
        <div>
          <div className="font-medium">{row.maquina_codigo}</div>
          <div className="text-sm text-gray-500">{row.maquina_descripcion}</div>
        </div>
      ),
    },
    {
      key: 'operador_info',
      title: t('reports.operator'),
      width: '160px',
      render: (_: unknown, row: ReporteBackend) => (
        <div>
          <div className="font-medium">{row.operador_codigo}</div>
          <div className="text-sm text-gray-500">{row.operador_descripcion}</div>
        </div>
      ),
    },
    {
      key: 'estado',
      title: t('reports.status'),
      width: '120px',
      render: (estado: string) => (
        <Badge variant={estado === 'APROBADO' ? 'success' : estado === 'PENDIENTE' ? 'warning' : undefined}>
          {estado}
        </Badge>
      ),
    },
  ], [t]);

  const filters = useMemo(() => [
    { key: 'fecha_inicio', label: t('analysis.date_from'), type: 'date' as const },
    { key: 'fecha_fin',    label: t('analysis.date_to'),   type: 'date' as const },
    { key: 'numero',       label: t('reports.report_number'), type: 'text' as const, placeholder: 'Ej: e000001' },
    { key: 'maquina_codigo',  label: t('reports.machine'),   type: 'text' as const, placeholder: 'Ej: MAQ001' },
    { key: 'operador_codigo', label: t('reports.operator'),  type: 'text' as const, placeholder: 'Ej: OP002' },
    {
      key: 'estado',
      label: t('reports.status'),
      type: 'select' as const,
      options: [
        { value: 'BORRADOR',  label: 'Borrador' },
        { value: 'PENDIENTE', label: t('reports.status_pending') },
        { value: 'APROBADO',  label: t('reports.status_approved') },
        { value: 'RECHAZADO', label: t('reports.status_rejected') },
      ],
    },
    {
      key: 'tiene_ubicacion',
      label: t('reports.location'),
      type: 'select' as const,
      options: [
        { value: 'true',  label: `${t('common.yes')} GPS` },
        { value: 'false', label: `${t('common.no')} GPS` },
      ],
    },
  ], [t]);
  
  // Estado local para filtros
  const [filtros, setFiltros] = useState<ReporteFiltros>({
    page: 1,
    per_page: 20,
  });

  // Estados para modales
  const [modalEliminarOpen, setModalEliminarOpen] = useState(false);
  const [reporteAEliminar, setReporteAEliminar] = useState<ReporteBackend | null>(null);
  const [modalPDFOpen, setModalPDFOpen] = useState(false);
  const [reporteParaPDF, setReporteParaPDF] = useState<ReporteBackend | null>(null);
  const [eliminandoReporte, setEliminandoReporte] = useState(false);

  // Datos de máquinas para filtros (usando hook multitenant)
  useListaMaquinas();

  // Hook multitenant para reportes
  const { 
    data: reportesData, 
    isLoading, 
    error,
    refetch 
  } = useReportes(filtros);

  // Datos de reportes (sin filtro adicional - el backend ya aplica el filtro)
  const reportesFiltrados = useMemo(() => {
    if (!reportesData?.items) {
      return [];
    }
    
    // El backend ya aplica el filtro por máquinas asignadas
    // No necesitamos filtrar nuevamente en el frontend
    return reportesData.items;
  }, [reportesData?.items]);

  // Crear data filtrada para el componente CrudTable
  const dataFiltrada = useMemo((): any => {
    return reportesData ? {
      ...reportesData,
      items: reportesFiltrados,
      total: reportesFiltrados.length
    } : {
      items: [],
      total: 0,
      page: 1,
      per_page: 20,
      pages: 0,
      has_next: false,
      has_prev: false,
    };
  }, [reportesData, reportesFiltrados]);


  // Verificar permisos - Solo basado en permisos UAU
  const puedeEditar = (reporte: ReporteBackend) => {
    if (!usuario) return false;
    if (reporte.estado === 'APROBADO') return false; // No editar aprobados
    
    // Solo verificar si tiene el permiso específico
    return hasPermission('reportes.editar');
  };

  const puedeEliminar = (reporte: ReporteBackend) => {
    if (!usuario) return false;
    if (reporte.estado === 'APROBADO') return false; // No eliminar aprobados
    
    // Solo verificar si tiene el permiso específico
    return hasPermission('reportes.eliminar');
  };

  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setFiltros({ 
      ...filtros, 
      ...newFilters, 
      page: 1
    });
  };

  const handlePageChange = (page: number) => {
    setFiltros({ ...filtros, page });
  };

  const handleAdd = () => {
    navigate('/reportes/crear');
  };

  const handleEdit = (reporte: ReporteBackend) => {
    if (!puedeEditar(reporte)) {
      toast.error('No tienes permisos para editar este reporte');
      return;
    }
    navigate(`/reportes/${reporte.id}/editar`);
  };

  const handleView = (reporte: ReporteBackend) => {
    navigate(`/reportes/${reporte.id}`);
  };

  const handleDeleteClick = (reporte: ReporteBackend) => {
    if (!puedeEliminar(reporte)) {
      toast.error('No tienes permisos para eliminar este reporte');
      return;
    }
    setReporteAEliminar(reporte);
    setModalEliminarOpen(true);
  };

  const confirmarEliminarReporte = async () => {
    if (!reporteAEliminar) return;

    setEliminandoReporte(true);
    
    try {
      // Usar ReportesService en lugar de fetch manual
      await ReportesService.eliminarReporte(reporteAEliminar.id);

      toast.success(`Reporte ${reporteAEliminar.numero} eliminado exitosamente`);
      refetch(); // Recargar lista
      setModalEliminarOpen(false);
      setReporteAEliminar(null);
      
    } catch (error: any) {
      console.error('Error eliminando reporte:', error);
      const mensaje = error?.message || 'Error al eliminar reporte';
      toast.error(mensaje);
    } finally {
      setEliminandoReporte(false);
    }
  };

  const cancelarEliminarReporte = () => {
    setModalEliminarOpen(false);
    setReporteAEliminar(null);
  };

  const handlePrintClick = (reporte: ReporteBackend) => {
    setReporteParaPDF(reporte);
    setModalPDFOpen(true);
  };

  const handleGenerarPDF = async (opciones: {
    tipo: 'frontend' | 'mapa' | 'filtros';
    incluirMapa: boolean;
    zoom: number;
  }) => {
    if (!reporteParaPDF) return;

    try {
      // Usar el servicio actualizado para descargar PDF
      const blob = await ReportesService.descargarPDF(reporteParaPDF.id, {
        incluirMapa: opciones.incluirMapa,
        zoom: opciones.zoom,
        lang: i18n.language,
      });
      
      // Crear enlace de descarga
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `reporte_${reporteParaPDF.numero}_${toISODateSafe(new Date())}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar URL temporal
      URL.revokeObjectURL(link.href);

      toast.success(`PDF del reporte ${reporteParaPDF.numero} descargado`);
      setModalPDFOpen(false);
      setReporteParaPDF(null);
      
    } catch (error: any) {
      console.error('Error generando PDF:', error);
      toast.error(`Error al generar PDF: ${error.message}`);
    }
  };

  const customActions = [
    {
      key: 'view',
      label: t('reports.report_detail'),
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      onClick: handleView,
      className: 'text-blue-600 hover:text-blue-900',
    },
    {
      key: 'edit',
      label: t('common.edit'),
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      onClick: handleEdit,
      className: 'text-green-600 hover:text-green-900',
      visible: (row: ReporteBackend) => puedeEditar(row),
    },
    {
      key: 'pdf',
      label: t('pdf.generate'),
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
        </svg>
      ),
      onClick: handlePrintClick,
      className: 'text-purple-600 hover:text-purple-900',
    },
    {
      key: 'delete',
      label: t('common.delete'),
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      ),
      onClick: handleDeleteClick,
      className: 'text-red-600 hover:text-red-900',
      visible: (row: ReporteBackend) => puedeEliminar(row),
    },
  ];

  const statsData = dataFiltrada ? [
    {
      label: t('analysis.total_reports'),
      value: dataFiltrada.total.toString(),
      color: 'bg-blue-100 text-blue-800',
    },
    {
      label: t('dashboard.total_hours'),
      value: dataFiltrada.items.reduce((acc: number, r: any) => acc + r.horas_trabajadas, 0).toFixed(1) + 'h',
      color: 'bg-green-100 text-green-800',
    },
    {
      label: t('reports.location'),
      value: dataFiltrada.items.filter((r: any) => r.tiene_ubicacion).length.toString(),
      color: 'bg-emerald-100 text-emerald-800',
    },
    {
      label: t('dashboard.total_area'),
      value: dataFiltrada.items.reduce((acc: number, r: any) => acc + r.total_area_trabajada, 0).toFixed(2) + ' ha',
      color: 'bg-purple-100 text-purple-800',
    },
    {
      label: t('dashboard.total_length'),
      value: (dataFiltrada.items.reduce((acc: number, r: any) => acc + r.total_longitud_metros, 0) / 1000).toFixed(2) + ' km',
      color: 'bg-orange-100 text-orange-800',
    },
  ] : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('reports.title')}</h1>
          <p className="text-gray-600">{t('reports.title')}</p>
        </div>
        
        <Button
          onClick={hasPermission('reportes.crear') ? handleAdd : undefined}
          disabled={!hasPermission('reportes.crear')}
          variant={hasPermission('reportes.crear') ? 'primary' : 'secondary'}
          title={!hasPermission('reportes.crear') ? 'No tienes permisos para crear reportes' : undefined}
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('reports.new_report')}
        </Button>
      </div>

      {statsData.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {statsData.map((stat, index) => (
            <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}



      {isLoadingFiltro && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            <p className="text-sm text-gray-600">
              Cargando permisos de máquinas...
            </p>
          </div>
        </div>
      )}

      <CrudTable
        data={dataFiltrada}
        isLoading={isLoading || isLoadingFiltro}
        error={error as Error | null}
        columns={columns}
        filters={filters}
        currentFilters={filtros}
        onFiltersChange={handleFiltersChange}
        onPageChange={handlePageChange}
        customActions={customActions}
        title={t('reports.title')}
      />

      {/* Modal Confirmar Eliminar Reporte */}
      <ModalConfirmarEliminar
        isOpen={modalEliminarOpen}
        titulo={t('reports.edit_report')}
        mensaje={reporteAEliminar
          ? `${t('reports.confirm_delete')} "${reporteAEliminar.numero}"?`
          : ""
        }
        detalles={reporteAEliminar ? [
          `${t('reports.machine')}: ${reporteAEliminar.maquina_codigo} - ${reporteAEliminar.maquina_descripcion}`,
          `${t('reports.operator')}: ${reporteAEliminar.operador_codigo} - ${reporteAEliminar.operador_descripcion}`,
          `${t('reports.date')}: ${formatDateSafe(reporteAEliminar.fecha)}`,
          `${t('reports.status')}: ${reporteAEliminar.estado}`,
          `${t('reports.worked_hours')}: ${reporteAEliminar.horas_trabajadas}h`,
          `${t('faenas.title')}: ${reporteAEliminar.total_faenas}`,
          reporteAEliminar.total_area_trabajada > 0
            ? `${t('faenas.worked_area')}: ${reporteAEliminar.total_area_trabajada} ha`
            : t('common.no_data'),
          `⚠️ ${t('reports.delete_kml_warning')}`,
          `⚠️ ${t('reports.delete_warning')}`,
        ] : []}
        onConfirmar={confirmarEliminarReporte}
        onCancelar={cancelarEliminarReporte}
        confirmText={eliminandoReporte ? t('reports.deleting') : t('reports.yes_delete')}
        cancelText={t('common.cancel')}
        tipo="danger"
        loading={eliminandoReporte}
      />

      {/* Modal Opciones PDF */}
      <ModalOpcionesPDF
        isOpen={modalPDFOpen}
        reporte={reporteParaPDF}
        onClose={() => {
          setModalPDFOpen(false);
          setReporteParaPDF(null);
        }}
        onGenerar={handleGenerarPDF}
      />
    </div>
  );
};

export default ReportesListView;